<template>
  <div class="fair-pay-page">
    <PageTitle title="Fair Pay" show_help_menu />
    <FairPayMenu
      :items="menuItems"
      :value="menuTab"
      @onChange="onTabChange"
      @onSameTabClick="onSameTabClick"
    />
    <router-view v-if="isDetailView" />
    <PayPlansListPage
      v-else-if="selectedTab === FAIR_PAY_TAB.PAY_PLANS"
      @envelope-counts="onEnvelopeCounts"
      @go-to-pay-ranges="onTabChange(FAIR_PAY_TAB.PAY_RANGES)"
    />
    <PositionsPayRangesPage v-else-if="selectedTab === FAIR_PAY_TAB.POSITION_PAY_RANGES" />
    <PayRangesListPage
      v-else-if="selectedTab === FAIR_PAY_TAB.PAY_RANGES"
      :active-operating-currency-count="activeOperatingCurrencyCount"
      :reference-currency-code="referenceCurrencyCode"
      :operating-countries="activeOperatingCountries"
      :currency-items="activeOperatingCurrencies"
    />
    <MarketDataPage v-else-if="selectedTab === FAIR_PAY_TAB.MARKET_DATA" />
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, watch } from "vue";
import PageTitle from "@components/common/PageTitle.vue";
import FairPayMenu from "@components/FairPayMenu/FairPayMenu.vue";
import {
  FAIR_PAY_TAB,
  buildFairPayMenuItems,
  fairPayListRoute,
  fairPayTabFromQuery,
} from "@components/FairPayMenu/fairPayMenuConstants";
import PayPlansListPage from "@components/PayPlansListPage/PayPlansListPage.vue";
import PositionsPayRangesPage from "@components/PositionsPayRangesPage/PositionsPayRangesPage.vue";
import PayRangesListPage from "@components/PayRangesListPage/PayRangesListPage.vue";
import MarketDataPage from "@components/MarketDataPage/MarketDataPage.vue";
import { listEmployeePayPlans } from "@/api/employeeAPI";
import { getOperatingCurrency, getOperatingCountry } from "@/api";
import { mapOperatingCurrencyRows } from "@/utils/operatingCurrencyMapper";
import { mapCountryPickerItem } from "@components/PayRangesListPage/composables/usePayRangesListPageState";
import { mergeLocationCountryConfig } from "@components/LocationPicker/locationCountryConfigs";

const { proxy } = getCurrentInstance() || {};
const ORGANIZATION_TAB_PARAMS = {
  [FAIR_PAY_TAB.JOB_FAMILIES]: "job-families",
  [FAIR_PAY_TAB.JOB_CATEGORIES]: "categories",
};
const selectedTab = ref(FAIR_PAY_TAB.PAY_PLANS);
const currentPayPlanCount = ref(null);
const activePayRangeCount = ref(null);
const activeOperatingCurrencyCount = ref(null);
const referenceCurrencyCode = ref(null);
const activeOperatingCountries = ref(null);
const activeOperatingCurrencies = ref([]);

onMounted(() => {
  const tabQuery = proxy?.$route?.query?.tab;
  if (isEmployeeDetail.value) {
    selectedTab.value = FAIR_PAY_TAB.PAY_PLANS;
  } else if (isPositionDetail.value) {
    selectedTab.value = FAIR_PAY_TAB.POSITION_PAY_RANGES;
  } else if (isPayRangeDetail.value) {
    selectedTab.value = FAIR_PAY_TAB.PAY_RANGES;
  } else {
    selectedTab.value = fairPayTabFromQuery(tabQuery) ?? FAIR_PAY_TAB.PAY_PLANS;
  }
  if (selectedTab.value !== FAIR_PAY_TAB.PAY_PLANS || isPayRangeDetail.value) {
    loadBadgeCounts();
  }
  loadActiveOperatingCurrencyInfo();
  loadActiveOperatingCountries();
});

const isPayRangeDetail = computed(() => proxy?.$route?.name === "FairPayPayRangeDetail");
const isEmployeeDetail = computed(() =>
  ["FairPayEmployee", "EmployeePayPlans"].includes(proxy?.$route?.name)
);
const isPositionDetail = computed(() => proxy?.$route?.name === "FairPayPosition");
const isDetailView = computed(
  () => isPayRangeDetail.value || isEmployeeDetail.value || isPositionDetail.value
);

/**
 * Keep the active in-page tab in sync when navigation updates `?tab=` without
 * going through `onTabChange` (for example the Position Fair Pay breadcrumb
 * returning to the Positions list from an Employee Pay Plans context).
 */
watch(
  () => proxy?.$route?.query?.tab,
  (tabQuery) => {
    if (tabQuery === undefined || tabQuery === null || tabQuery === "") return;
    selectedTab.value = fairPayTabFromQuery(tabQuery) ?? FAIR_PAY_TAB.PAY_PLANS;
  }
);

const menuTab = computed(() => {
  if (isPayRangeDetail.value) return FAIR_PAY_TAB.PAY_RANGES;
  if (isEmployeeDetail.value) return FAIR_PAY_TAB.PAY_PLANS;
  if (isPositionDetail.value) return FAIR_PAY_TAB.POSITION_PAY_RANGES;
  return selectedTab.value;
});

const menuItems = computed(() =>
  buildFairPayMenuItems({
    currentPayPlanCount: currentPayPlanCount.value,
    activePayRangeCount: activePayRangeCount.value,
  })
);

function onEnvelopeCounts({ currentPayPlanCount: payPlans, activePayRangeCount: payRanges } = {}) {
  if (payPlans != null) currentPayPlanCount.value = payPlans;
  if (payRanges != null) activePayRangeCount.value = payRanges;
}

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

async function loadBadgeCounts() {
  const accountId = getAccountId();
  if (!accountId) return;
  try {
    const response = await listEmployeePayPlans(accountId, { page: 1, pageSize: 10 });
    onEnvelopeCounts({
      currentPayPlanCount: response?.data?.current_pay_plan_count,
      activePayRangeCount: response?.data?.active_pay_range_count,
    });
  } catch {
    /* badges stay empty until the Pay Plans tab loads */
  }
}

const ACTIVE_CURRENCY_PAGE_SIZE = 50;
const MAX_REFERENCE_CURRENCY_PAGES = 10;

/** OC Rule 17 — count of active operating currencies for the reference-currency toggle,
 *  plus the account's reference (base) currency code for its label (ER-11233), plus the full
 *  active operating-currency rows (threaded down to the Pay Range wizard for the single-currency
 *  auto-default/disable UX, ER-11873).
 *  Retired currencies (active=false) do not count; the reference currency itself always does.
 *  The reference row carries is_reference=true, so page through the active rows until found. */
async function loadActiveOperatingCurrencyInfo() {
  const accountId = getAccountId();
  if (!accountId) return;
  try {
    let offset = 0;
    let total = null;
    const collected = [];
    for (let page = 0; page < MAX_REFERENCE_CURRENCY_PAGES; page += 1) {
      const response = await getOperatingCurrency(accountId, {
        offset,
        size: ACTIVE_CURRENCY_PAGE_SIZE,
        status: ["active"],
      });
      const data = response?.data;
      if (total === null) {
        const count = Number(data?.count);
        activeOperatingCurrencyCount.value = Number.isFinite(count) ? count : null;
        total = Number.isFinite(count) ? count : Infinity;
      }
      const rows = Array.isArray(data?.results) ? data.results : [];
      collected.push(...rows);
      const referenceCode = referenceCurrencyCodeOf(data);
      if (referenceCode && !referenceCurrencyCode.value) {
        referenceCurrencyCode.value = referenceCode;
      }
      offset += rows.length || 0;
      if (!rows.length || offset >= total) break;
    }
    // ER-11873 (fix): map the raw API rows to CurrencyPicker's shape (name/code/symbol)
    // — the same one-to-one mapping CurrencyPicker applies when it self-fetches
    // (`normalizeCurrencyRows` → `mapOperatingCurrencyRow`). Passing raw rows here left the
    // Used Currency dropdown items blank (raw rows carry currency_name/currency_code, not
    // name/code) and broke search. Mirrors loadActiveOperatingCountries() below.
    activeOperatingCurrencies.value = mapOperatingCurrencyRows(collected);
  } catch {
    /* unknown count / code -> render the toggle as before */
  }
}

/** First active row carrying `is_reference: true` -> its currency code, else null. */
function referenceCurrencyCodeOf(data) {
  const rows = Array.isArray(data?.results) ? data.results : [];
  const reference = rows.find((row) => row.is_reference === true);
  return reference?.currency_code || reference?.code || reference?.currency?.code || null;
}

const ACTIVE_COUNTRY_PAGE_SIZE = 50;
const MAX_OPERATING_COUNTRY_PAGES = 10;

/**
 * Map an operating-country row → LocationPicker CountryConfig, merging the three-level
 * scoping metadata from LocationPicker's known config table. A country the config table
 * does not know is still kept as a valid Country-only entry (no region/sub-region levels).
 */
function toLocationCountryConfig(row) {
  const item = mapCountryPickerItem(row);
  if (!item) return null;
  return mergeLocationCountryConfig(item);
}

/** ER-11872 — active Operating Countries for the Add/Edit Pay Range Draft wizard's
 *  Country field. Page through the active rows (same pattern as the currency loader). */
async function loadActiveOperatingCountries() {
  const accountId = getAccountId();
  if (!accountId) return;
  try {
    const collected = [];
    let offset = 0;
    let total = null;
    for (let page = 0; page < MAX_OPERATING_COUNTRY_PAGES; page += 1) {
      const response = await getOperatingCountry(accountId, {
        offset,
        size: ACTIVE_COUNTRY_PAGE_SIZE,
        status: ["active"],
      });
      const data = response?.data;
      if (total === null) {
        const count = Number(data?.count);
        total = Number.isFinite(count) ? count : null;
      }
      const rows = Array.isArray(data?.results) ? data.results : [];
      collected.push(...rows);
      offset += rows.length;
      if (!rows.length) break;
      if (total !== null && offset >= total) break;
    }
    activeOperatingCountries.value = collected.map(toLocationCountryConfig).filter(Boolean);
  } catch {
    activeOperatingCountries.value = null;
  }
}

function openOrganizationTab(index) {
  proxy?.$router
    ?.push({ name: "NewOrganization", params: { tab: ORGANIZATION_TAB_PARAMS[index] } })
    .catch(() => {});
}

/**
 * Build the list-route query for a tab. Returning to the Positions tab from a
 * Position card keeps its as-of date so the row still renders the subscription
 * card (including future-dated subscriptions) instead of "+ Subscribe".
 */
function listRouteForTab(index) {
  const context = {};
  if (
    index === FAIR_PAY_TAB.POSITION_PAY_RANGES &&
    isPositionDetail.value &&
    proxy?.$route?.query?.asOf
  ) {
    context.asOf = proxy.$route.query.asOf;
  }
  return fairPayListRoute(index, context);
}

function onTabChange(index) {
  if (index in ORGANIZATION_TAB_PARAMS) {
    openOrganizationTab(index);
    return;
  }
  selectedTab.value = index;
  if (isDetailView.value) {
    proxy?.$router?.push(listRouteForTab(index)).catch(() => {});
  } else {
    proxy?.$router?.replace(listRouteForTab(index)).catch(() => {});
  }
}

function onSameTabClick(index) {
  if (index in ORGANIZATION_TAB_PARAMS) {
    openOrganizationTab(index);
    return;
  }
  if (isDetailView.value) {
    proxy?.$router?.push(listRouteForTab(index)).catch(() => {});
  }
}
</script>

<style scoped>
.fair-pay-page {
  position: relative;
  top: 15px;
  width: 100%;
  padding: 16px;
}
</style>
